import { Box, Stack, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import {
  EOp,
  EPartnerCodeStatusOptions,
  EPartnerCodeTypeOptions,
  FilterModelText,
  Nullish,
} from 'types'

interface FilterOption {
  value: string
  label: string
}

/**
 * Toolbar dropdown backed by a server-side `equals` filter. Code type and status are short closed
 * lists, so a select reads better than the free-text column filters used for code and organization.
 */
function useSelectFilter(label: string, name: string, options: readonly FilterOption[]) {
  const [selected, setSelected] = useState<Nullish<FilterOption>>(null)

  const filterValue: Nullish<FilterModelText> = useMemo(() => {
    if (!selected) {
      return null
    }

    return {
      type: 'text',
      op: EOp.equals,
      value: selected.value,
    } as FilterModelText
  }, [selected])

  const handleChange = useCallback((_: SyntheticEvent, value: Nullish<FilterOption>) => {
    setSelected(value)
  }, [])

  const control = useMemo(
    () => (
      <Stack direction="row" alignItems="center" className="gap-8">
        <Typography sx={{ whiteSpace: 'nowrap' }}>{label}</Typography>

        {/* Fixed width keeps the dropdowns equal whatever the label length. */}
        <Autocomplete
          multiple={false}
          options={options as FilterOption[]}
          value={selected}
          onChange={handleChange}
          sx={{ width: 220 }}
          renderInput={(params) => <TextField {...params} size="small" name={name} />}
        />
      </Stack>
    ),
    [handleChange, label, name, options, selected],
  )

  return { control, filterValue }
}

export function usePartnerCodeFilters() {
  const codeType = useSelectFilter('Code Type', 'codeType', EPartnerCodeTypeOptions)
  const status = useSelectFilter('Status', 'status', EPartnerCodeStatusOptions)

  const PartnerCodeFilters = useMemo(
    () => (
      <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
        <Box minWidth="280px">{codeType.control}</Box>
        <Box minWidth="280px">{status.control}</Box>
      </Stack>
    ),
    [codeType.control, status.control],
  )

  return {
    PartnerCodeFilters,
    codeTypeFilterValue: codeType.filterValue,
    statusFilterValue: status.filterValue,
  }
}
